
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import dayjs from "dayjs";
import {
  formatDurationSeconds,
  getExperiments,
  getPageFlow,
  getPageViewsCount,
  getSessionDuration,
} from "./helpers";
import { Column } from "./types";

export const columns: Column[] = [
  {
    id: "sessionStart",
    label: "Start Time",
    minWidth: 150,
    sortable: true,
    filterable: true,
    format: (value) => dayjs(value as number).format("MMM D, YYYY HH:mm"),
    getValue: (s) => s.sessionStart,
  },
  {
    id: "sessionDuration",
    label: "Duration",
    minWidth: 80,
    align: "right",
    sortable: true,
    format: (_, session) => formatDurationSeconds(getSessionDuration(session)),
    getValue: (s) => getSessionDuration(s),
  },
  {
    id: "pagesViewed",
    label: "Pages",
    minWidth: 60,
    align: "right",
    sortable: true,
    format: (_, session) => getPageViewsCount(session),
    getValue: (s) => getPageViewsCount(s),
  },
  {
    id: "landingPage",
    label: "Landing Page",
    minWidth: 150,
    sortable: true,
    filterable: true,
    format: (_, session) => {
      const pageFlow = getPageFlow(session);
      const landing = pageFlow[0] || "-";
      return (
        <Tooltip title={landing}>
          <Typography
            variant="body2"
            sx={{
              maxWidth: 150,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {landing}
          </Typography>
        </Tooltip>
      );
    },
    getValue: (s) => getPageFlow(s)[0] || "",
  },
  {
    id: "country",
    label: "Country",
    minWidth: 100,
    sortable: true,
    filterable: true,
    format: (value) => (value as string) || "-",
    getValue: (s) => s.country || "",
  },
  {
    id: "region",
    label: "Region",
    minWidth: 100,
    sortable: true,
    filterable: true,
    format: (value) => (value as string) || "-",
    getValue: (s) => s.region || "",
  },
  {
    id: "city",
    label: "City",
    minWidth: 100,
    sortable: true,
    filterable: true,
    format: (value) => (value as string) || "-",
    getValue: (s) => s.city || "",
  },
  {
    id: "timezone",
    label: "Timezone",
    minWidth: 120,
    sortable: true,
    filterable: true,
    format: (value) => (value as string) || "-",
    getValue: (s) => s.timezone || "",
  },
  {
    id: "device",
    label: "Device",
    minWidth: 80,
    sortable: true,
    filterable: true,
    format: (_, session) => {
      const width = session.screenWidth;
      if (!width) return "-";
      if (width < 768) return "Mobile";
      if (width < 1024) return "Tablet";
      return "Desktop";
    },
    getValue: (s) => {
      const width = s.screenWidth;
      if (!width) return "";
      if (width < 768) return "Mobile";
      if (width < 1024) return "Tablet";
      return "Desktop";
    },
  },
  {
    id: "screenWidth",
    label: "Screen",
    minWidth: 100,
    align: "right",
    sortable: true,
    format: (_, session) => {
      if (!session.screenWidth || !session.screenHeight) return "-";
      return `${session.screenWidth}×${session.screenHeight}`;
    },
    getValue: (s) => s.screenWidth || 0,
  },
  {
    id: "referrer",
    label: "Referrer",
    minWidth: 150,
    sortable: true,
    filterable: true,
    format: (value) => {
      const ref = value as string;
      if (!ref) return "(direct)";
      return (
        <Tooltip title={ref}>
          <Typography
            variant="body2"
            sx={{
              maxWidth: 150,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {ref}
          </Typography>
        </Tooltip>
      );
    },
    getValue: (s) => s.referrer || "",
  },
  {
    id: "linkSource",
    label: "Link Source",
    minWidth: 120,
    sortable: true,
    filterable: true,
    format: (value) => {
      const source = value as string;
      if (!source) return "-";
      return (
        <Tooltip title={source}>
          <Chip
            label={source}
            size="small"
            variant="outlined"
            color="secondary"
          />
        </Tooltip>
      );
    },
    getValue: (s) => s.linkSource || "",
  },
  {
    id: "userAgent",
    label: "User Agent",
    minWidth: 150,
    sortable: true,
    filterable: true,
    format: (value) => {
      const ua = value as string;
      if (!ua) return "-";
      return (
        <Tooltip title={ua}>
          <Typography
            variant="body2"
            sx={{
              maxWidth: 150,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {ua}
          </Typography>
        </Tooltip>
      );
    },
    getValue: (s) => s.userAgent || "",
  },
  {
    id: "persona",
    label: "Persona",
    minWidth: 100,
    sortable: true,
    filterable: true,
    format: (value) => (value as string) || "-",
    getValue: (s) => s.persona || "",
  },
  {
    id: "experiment",
    label: "Experiments",
    minWidth: 140,
    sortable: true,
    filterable: true,
    format: (_, session) => {
      const experiments = getExperiments(session);
      if (experiments.length === 0) return "-";
      return (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {experiments.map((exp) => (
            <Chip
              key={`${exp.experimentId}:${exp.variantId}`}
              label={`${exp.experimentId}: ${exp.variantId}`}
              size="small"
              variant="outlined"
              color={exp.variantId === "control" ? "default" : "primary"}
            />
          ))}
        </Box>
      );
    },
    getValue: (s) =>
      getExperiments(s)
        .map((e) => `${e.experimentId}:${e.variantId}`)
        .join(", "),
  },
  {
    id: "signedUp",
    label: "Signed Up",
    minWidth: 80,
    align: "center",
    sortable: true,
    filterable: true,
    format: (value) =>
      value ? (
        <Chip label="Yes" size="small" color="success" />
      ) : (
        <Chip label="No" size="small" variant="outlined" />
      ),
    getValue: (s) => s.signedUp,
  },
  {
    id: "contacted",
    label: "Contacted",
    minWidth: 80,
    align: "center",
    sortable: true,
    filterable: true,
    format: (value) =>
      value ? (
        <Chip label="Yes" size="small" color="success" />
      ) : (
        <Chip label="No" size="small" variant="outlined" />
      ),
    getValue: (s) => s.contacted,
  },
  {
    id: "email",
    label: "Email",
    minWidth: 150,
    sortable: true,
    filterable: true,
    format: (value) => (value as string) || "-",
    getValue: (s) => s.email || "",
  },
  {
    id: "sessionId",
    label: "Session ID",
    minWidth: 120,
    filterable: true,
    format: (value) => (
      <Tooltip title={value as string}>
        <Typography
          variant="body2"
          sx={{
            maxWidth: 120,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontFamily: "monospace",
            fontSize: 11,
          }}
        >
          {value as string}
        </Typography>
      </Tooltip>
    ),
    getValue: (s) => s.sessionId,
  },
];

import { SegmentFilterType } from "../../types/analytics";

export const columnToSegmentFilterType: Record<
  string,
  SegmentFilterType | null
> = {
  sessionStart: "session_start",
  sessionDuration: "session_duration",
  pagesViewed: "pages_viewed",
  landingPage: "landing_page",
  country: "country",
  region: "region",
  city: "city",
  timezone: "timezone",
  device: "device",
  screenWidth: "screen_width",
  referrer: "referrer",
  linkSource: "source",
  userAgent: "user_agent",
  persona: "persona",
  experiment: "experiment",
  signedUp: "signed_up",
  contacted: "contacted",
  email: "email",
  sessionId: null, // Not suitable for segment filtering
};
